/**
 * Maps a tenant's stored theme onto the page. Two consumers:
 *  - the shadcn CSS variables, one per token (never a tenant-supplied name)
 *  - the LootTheme prop for <LootProvider> (--los-* vars inside the opener)
 *
 * The sanitizing resolver itself lives in `@loot-os/api-contract`
 * (`resolveStorefrontThemeVars`), shared with the dashboard's box preview, which
 * scopes a tenant theme to one container instead of the whole page. What stays
 * here is the DOM half — writing and, importantly, *clearing* the variables —
 * plus the bridge to the opener's own theme API.
 */
import {
  type CookieBannerTheme,
  inferColorScheme,
  resolveCookieBannerThemeVars,
  resolveOpenerTheme,
  resolveStorefrontThemeVars,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import type { LootTheme } from "@loot-os/react";
import { loadStorefrontFonts } from "@loot-os/ui/lib/storefrontFonts";

/** Applies (or, with undefined values, clears) the tenant theme on <html>. */
export function applyPageTheme(theme: StorefrontTheme | undefined): void {
  const style = document.documentElement.style;
  // Cleared first: these three are the only ones that can be *absent* from the
  // resolved set, so a previous tenant's values would otherwise survive.
  style.removeProperty("--radius");
  style.removeProperty("font-family");
  style.removeProperty("--ds-font-heading");

  const { colors, radius, fontFamily, headingFontFamily, fonts } =
    resolveStorefrontThemeVars(theme);
  // Unset tokens resolve to the stock palette, so the page always gets a full set.
  for (const [name, value] of Object.entries(colors)) {
    style.setProperty(name, value);
  }
  if (radius) style.setProperty("--radius", radius);
  if (fontFamily) style.setProperty("font-family", fontFamily);
  // h1–h3 read it (globals.css) and fall back to the body font while it is unset.
  if (headingFontFamily) style.setProperty("--ds-font-heading", headingFontFamily);
  // The stacks already name the families these register; until a file arrives the text shows
  // in the stack's fallback (font-display: swap). The loader never rejects.
  void loadStorefrontFonts(fonts);

  // Native chrome (select popups, scrollbars, checkboxes) follows color-scheme,
  // which globals.css and index.html pin to dark. A light palette needs it
  // flipped or every popup renders dark on a light page; an inline declaration
  // on <html> outranks both. A non-hex background can't be judged, so it leaves
  // the stylesheet default in charge.
  const scheme = inferColorScheme(colors["--background"]);
  if (scheme) style.setProperty("color-scheme", scheme);
  else style.removeProperty("color-scheme");
}

/**
 * Writes the cookie banner's theme onto <html> as `--cookie-banner-*`, beside the page theme:
 * `termly.css` maps them onto Termly's banner and preference modal. Always the complete set,
 * with an unset token as `var(--<store token>)`, so a tenant with no banner theme keeps getting
 * a banner that follows the store, and a previous tenant's values never survive.
 */
export function applyCookieBannerTheme(theme: CookieBannerTheme | undefined | null): void {
  const style = document.documentElement.style;
  const { colors, radius } = resolveCookieBannerThemeVars(theme);
  for (const [name, value] of Object.entries(colors)) {
    style.setProperty(name, value);
  }
  style.setProperty("--cookie-banner-radius", radius);
}

/**
 * The same stored shape drives the opener. The token → opener-color mapping
 * lives in api-contract, shared with the dashboard's box preview, which mounts
 * the same opener and would otherwise drift from this one.
 */
export function toLootTheme(theme: StorefrontTheme | undefined): LootTheme | undefined {
  return resolveOpenerTheme(theme);
}
